import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import { ThemeProvider } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import cn from 'classnames';
import { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import { AiFillInfoCircle } from 'react-icons/ai';
import { BsFillArrowRightCircleFill } from 'react-icons/bs';
import Switch from 'react-switch';

import { checkCoupon } from '../../api/wave/coupon';
import { cartPrice } from '../../store/cart';
import { Order, CouponDiscount } from '../../store/order';
import User from '../../store/user';
import { theme } from '../../utils/themeProvider';
import styles, { boxShadow, brandColor, brandColorMuted } from '../OrderDeliveryForm/OrderPreferences.module.scss';

function OrderCouponForm({ control, couponNo, guestUser }) {
  const [couponUse, setCouponUse] = useState(false);
  const [couponError, setCouponError] = useState('');
  const [couponSuccess, setCouponSuccess] = useState('');

  const REQUIRED_FIELD = 'Υποχρεωτικό πεδίο';

  const errorMessages = {
    couponNo: {
      required: REQUIRED_FIELD,
      minLength: 'Μη έγκυρη τιμή',
    },
  };

  function onChangeCouponUse() {
    setCouponUse(!couponUse);
    if (couponUse) {
      Order.setCouponCode(undefined);
      CouponDiscount.update(0);
    }
  }

  function onKeyPressCoupon(e) {
    if (e.key === 'Enter') e.preventDefault();
  }

  async function onClickCouponArrow() {
    const _order = Order.getValue();
    if (_order?.couponCode) return;
    const couponText = couponNo.toUpperCase();
    if (couponText.length > 4) {
      setCouponError('');
      setCouponSuccess('');
      const price = cartPrice.getValue();
      try {
        const user = User.getValue();
        const token = user?.authToken;
        const res = await checkCoupon(couponText, token);
        if (res.offerType === 'amount') {
          if (res.price > price) {
            setCouponError('Η αξία του κουπονίου δεν μπορεί να είναι μεγαλύτερη από την τιμή του καλαθιού');
            CouponDiscount.update(0);
            return;
          }
          setCouponSuccess(`To κουπόνι σου επιβεβαιώθηκε! \n Κερδίζεις έκπτωση ${(res.price / 100).toFixed(2)}€!`);
          CouponDiscount.update(res.price);
          Order.setCouponCode(couponText);
          return;
        }
        if (res.offerType === 'percentage') {
          const discount = (price * res.price) / 100;
          if (discount > price) {
            setCouponError('Η αξία του κουπονίου δεν μπορεί να είναι μεγαλύτερη από την τιμή του καλαθιού');
            CouponDiscount.update(0);
            return;
          }
          setCouponSuccess(`To κουπόνι σου επιβεβαιώθηκε! \n Κερδίζεις έκπτωση ${(discount / 100).toFixed(2)}€!`);
          CouponDiscount.update(discount);
          Order.setCouponCode(couponText);
          setCouponError('');
          return;
        }
      } catch (err) {
        setCouponSuccess('');
        CouponDiscount.update(0);
        setCouponError(err?.error?.userMessage);
      }
    }
    CouponDiscount.update(0);
  }

  useEffect(() => {
    setCouponSuccess('');
  }, [couponNo]);

  return (
    <div className={styles.checkBoxContainer}>
      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
      <label className={styles.checkBoxLabel}>
        <span className={cn(styles.labelText, { [styles.opacity]: guestUser?.email })}>
          Κουπόνι έκπτωσης{' '}
          {guestUser?.email && (
            <span title="Κάνε εγγραφή και χρησιμοποίησε το κουπόνι έκπτωσης">
              <AiFillInfoCircle className={styles.infoIcon} />
            </span>
          )}
        </span>
        <Switch
          disabled={Boolean(guestUser?.email)}
          checked={couponUse}
          onChange={onChangeCouponUse}
          boxShadow={boxShadow}
          activeBoxShadow={boxShadow}
          onColor={brandColorMuted}
          onHandleColor={brandColor}
          handleDiameter={20}
          uncheckedIcon={false}
          checkedIcon={false}
          height={14}
          width={32}
          className="react-switch"
        />
      </label>
      <p className={cn(styles.details, { [styles.opacity]: guestUser?.email })}>
        Εισάγετε το κουπόνι στο παρακάτω πεδίο και πατήστε το βελάκι ώστε να εφαρμοστεί η έκπτωση.
      </p>
      <div
        className={cn(styles.couponInputContainer, {
          [styles.hideOnUnselected]: !couponUse,
          [styles.appearOnSelected]: couponUse,
        })}
      >
        <Box sx={{ height: '5.5rem', width: '20rem' }} className={styles.box}>
          <ThemeProvider theme={theme}>
            <Controller
              control={control}
              name="couponNo"
              defaultValue=""
              rules={{
                required: couponUse,
                minLength: 5,
              }}
              render={({ field: { ref, onChange, ...field }, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label="Αριθμός κουπονιού"
                  fullWidth
                  variant="outlined"
                  onChange={onChange}
                  onKeyPress={onKeyPressCoupon}
                  error={error !== undefined || Boolean(couponError)}
                  helperText={(error && errorMessages.couponNo[error.type]) || couponError}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <button type="button" onClick={onClickCouponArrow} className={styles.resetButton}>
                          <BsFillArrowRightCircleFill className={styles.couponArrow} />
                        </button>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </ThemeProvider>
          <p className={styles.couponSuccess}>{couponSuccess}</p>
        </Box>
      </div>
    </div>
  );
}

export default OrderCouponForm;
